<template>
	<q-page>
		<q-spinner-cube v-if="isLoading" color="primary" size="5em" class="absolute-center"/>
		<div v-else-if="displayedWatchlist">
			<div class="row q-col-gutter-md q-mb-md">
				<div class="col-12 col-sm-6">
					<h4 v-if="!displayedWatchlist.searchParams">{{ displayedWatchlist.name }}</h4>

					<ul>
						<li v-if="displayedWatchlist.zohoCustomer && user && !isPublic">{{ $t('customer') }}: {{
							displayedWatchlist.zohoCustomer }} - {{ displayedWatchlist.customerName }}
						</li>
						<li v-if="displayedWatchlist.totalArea">{{ $t('resultArea') }}: {{ displayedWatchlist.totalArea |
							area }}
						</li>
						<li v-if="displayedWatchlist.totalPrice && !isPublic">{{ $t('label.price')}}: {{
							displayedWatchlist.totalPrice | currency }}
						</li>
					</ul>
				</div>

				<div class="col-12 col-sm-6">
					<div class="row justify-between">
						<div class="column">
							<template v-if="displayedWatchlist.items.length">
								<div class="col">
									<span class="text-overline">{{ $t('sorting') }}</span>
								</div>
								<div class="col">
									<q-btn-dropdown split
													color="primary"
													:label="$t('button.watchlistSorting.' + sortingProperty)"
													:icon="sortingIcon"
													@click="changeSortingDirection"
													no-caps
									>
										<q-list>
											<q-item>
												<q-item-section clickable v-close-popup @click="setSorting('added')">
													<q-item-label>{{ $t('added') }}</q-item-label>
												</q-item-section>
											</q-item>
											<q-item>
												<q-item-section clickable v-close-popup @click="setSorting('logArea')">
													<q-item-label>{{ $t('resultArea') }}</q-item-label>
												</q-item-section>
											</q-item>
											<q-item>
												<q-item-section clickable v-close-popup
																@click="setSorting('customerQualityName')">
													<q-item-label>{{ $t('quality') }}</q-item-label>
												</q-item-section>
											</q-item>
											<q-item>
												<q-item-section clickable v-close-popup @click="setSorting('logNo')">
													<q-item-label>{{ $t('logNo') }}</q-item-label>
												</q-item-section>
											</q-item>
											<q-item>
												<q-item-section clickable v-close-popup @click="setSorting('palletNo')">
													<q-item-label>{{ $t('palletNo') }}</q-item-label>
												</q-item-section>
											</q-item>
										</q-list>
									</q-btn-dropdown>
								</div>
							</template>
						</div>

						<div class="column q-mt-md">
							<q-btn color="primary" icon="more_vert" class="float-right"
								   v-if="user || !displayedWatchlist.dynamic" outline dense>
								<q-menu>
									<q-list>
										<q-item @click="editWatchlist(displayedWatchlist)" v-if="user && !isPublic" clickable v-close-popup>
											<q-item-section>{{ $t('button.watchlistSettings') }}</q-item-section>
										</q-item>
										<q-item @click="share(displayedWatchlist)" v-if="user && !isPublic" clickable
												v-close-popup>
											<q-item-section>{{ $t('button.share') }}</q-item-section>
										</q-item>
										<q-item @click="downloadWatchlistSummary(displayedWatchlist)"
												v-if="!displayedWatchlist.searchParams" clickable v-close-popup>
											<q-item-section>{{ $t('button.downloadWatchlistSummary') }}</q-item-section>
										</q-item>
										<q-item @click="newVirtualLog"
												v-if="user && !isPublic && !displayedWatchlist.searchParams" clickable
												v-close-popup>
											<q-item-section>{{ $t('button.newVirtualLog') }}</q-item-section>
										</q-item>
										<q-item @click="deleteWatchlist(displayedWatchlist)" v-if="user && !isPublic"
												clickable v-close-popup>
											<q-item-section>{{ $t('button.deleteWatchlist') }}</q-item-section>
										</q-item>
										<q-item @click="startSearchParamsEdit"
												v-if="user && !isPublic && displayedWatchlist.searchParams" clickable
												v-close-popup>
											<q-item-section>{{ $t('button.editSearchParams') }}</q-item-section>
										</q-item>
									</q-list>
								</q-menu>
							</q-btn>
						</div>
					</div>
				</div>
			</div>

			<template v-if="!displayedWatchlist.dynamic">
				<div v-if="displayedWatchlist.items.length" class="row q-col-gutter-md">
					<watchlist-item v-for="item in sortedWatchlistItems"
									:key="item.uid"
									:item="item"
									:is-public="isPublic"
									class="col-12 col-sm-6 col-md-4 col-lg-4 col-xl-3"
					/>
				</div>
				<div v-else class="row q-col-gutter-md q-mt-lg justify-center">
					<div v-if="user && !isPublic">
						<q-btn @click="$router.push({name:'search'})"
							   :label="$t('button.newWatchlistAddFromSearch')"
							   color="accent"
							   text-color="accentText"
							   size="xl"
							   class="large-cta-btn"
							   no-caps
						/>
					</div>
					<div v-if="user && !isPublic">
						<q-btn @click="$router.push({name:'watchlists'})"
							   :label="$t('button.newWatchlistAddFromWatchlist')"
							   color="accent"
							   text-color="accentText"
							   size="xl"
							   class="large-cta-btn"
							   no-caps
						/>
					</div>
				</div>
			</template>
			<template v-else>
				<log-search
						v-if="(displayedWatchlist.searchParams || watchlistSearchParams) && !selectingParamsFromWatchlist"
						:is-dynamic-watchlist="true"
						:is-public="isPublic"
				/>
				<div v-else class="row q-col-gutter-md q-mt-lg justify-center">
					<template v-if="!selectingParamsFromWatchlist">
						<div v-if="user && !isPublic">
							<q-btn @click="startSearchParamsEdit"
								   :label="$t('button.newWatchlistSearchParametersFromSearch')"
								   color="accent"
								   text-color="accentText"
								   size="xl"
								   class="large-cta-btn"
								   no-caps
							/>
						</div>
						<div v-if="user && !isPublic">
							<q-btn @click="selectSearchParams"
								   :label="$t('button.newWatchlistSearchParametersFromWatchlist')"
								   color="accent"
								   text-color="accentText"
								   size="xl"
								   class="large-cta-btn"
								   no-caps
							/>
						</div>
					</template>
					<template v-else>
						<watchlists emit parameter-search @select-watchlist="setSearchParams"/>
					</template>
				</div>
			</template>
		</div>
		<div v-else>
			<h2 class="text-center">{{ $t('watchlistUnavailable') }}</h2>
		</div>
	</q-page>
</template>

<script>
	import {mapGetters} from 'vuex';
	import {FETCH_WATCHLIST, GET_FILTER_DATA} from "@/store/actions.type";
	import {
		RESET_SEARCH_PARAMS,
		RESET_SEARCH_RESULTS,
		SET_DETAIL_VIEW_BACK_LINK,
		SET_DISPLAY_ADVANCED_SEARCH,
		SET_EDIT_SEARCH_PARAMS,
		SET_SEARCH_PARAMS,
		SET_WATCHLIST,
		SET_WATCHLIST_SEARCH_PARAMS,
    SET_WATCHLISTS,
		START_VIRTUAL_LOG_CREATION
	} from "@/store/mutations.type";
	import WatchlistItem from "@/components/watchlist/WatchlistItem";
	import attributeMapping from "@/common/tpcloudAttributeMapping";
	import _isNumber from "lodash/isNumber";
	import VeneerLog from "@/components/common/VeneerLog";
	import NumberFormattingFilters from "@/mixins/NumberFormattingFilters";
	import LogUtility from "@/mixins/LogUtility";
	import LogSearch from "@/views/LogSearch";
	import WatchlistActions from "@/mixins/WatchlistActions";
	import Watchlists from "@/views/Watchlists";
  import imagetypes from "@/common/imagetypes";

	export default {
		name: "Watchlist",
		components: {Watchlists, LogSearch, VeneerLog, WatchlistItem},
		props: ['id', 'entityId', 'hash', 'propWatchlist'],
		mixins: [NumberFormattingFilters, LogUtility, WatchlistActions],
		data() {
			return {
				isLoading: false,
				sortingProperty: 'added',
				sortingDirection: 'DESC',
				selectingParamsFromWatchlist: false,
				resetWatchlistsBeforeLeave: false
			}
		},
		watch: {
			editSearchParams(newValue, oldValue) {
				if(oldValue && !newValue) {
					this.selectingParamsFromWatchlist = false;
				}
			},
			speciesList() {
				if(this.displayedWatchlist && this.displayedWatchlist.dynamic) {
					this.setSpeciesName();
				}
			},
      async displayedWatchlist(newValue, oldValue) {
        if(oldValue && newValue.showBundleImages !== oldValue.showBundleImages) {
          await this.setSearchParams(this.displayedWatchlist);
        }
      }
		},
		computed: {
			...mapGetters(['watchlist', 'displayedWatchlist', 'user', 'watchlistSearchParams', 'editSearchParams', 'speciesList']),
			sortedWatchlistItems() {
				if (this.sortingProperty && this.sortingProperty !== 'added' && this.displayedWatchlist.items.length > 0) {
					let items = [...this.displayedWatchlist.items];

					// get proper sorting property name
					const
						sortingProperty = (this.getConcreteItem(items[0])[this.sortingProperty] ?
							this.sortingProperty :
							attributeMapping[this.sortingProperty]),
						sortNumerical = _isNumber(this.getConcreteItem(items[0])[this.sortingProperty]);

					return items.sort((a, b) => {
						return this.getConcreteItem((this.sortingDirection === 'ASC' ? a : b))[sortingProperty].toString().localeCompare(
							this.getConcreteItem((this.sortingDirection === 'ASC' ? b : a))[sortingProperty].toString(),
							undefined,
							{numeric: sortNumerical}
						);
					});
				}

				if (this.sortingProperty === 'added' && this.sortingDirection === 'ASC') {
					return [...this.displayedWatchlist.items].reverse();
				}

				return this.displayedWatchlist.items;
			},
			// is public view reached through shared watchlist
			isPublic() {
				return this.$route.meta.hasOwnProperty('isPublic') && this.$route.meta.isPublic;
			},
			sortingIcon() {
				return this.sortingDirection === 'DESC' ? 'arrow_downward' : 'arrow_upward';
			}
		},
		async mounted() {
			let backlink = false;

			if (!this.propWatchlist || !this.displayedWatchlist.items) {
				this.isLoading = true;
				if (this.id) {
					await this.$store.dispatch(FETCH_WATCHLIST, {watchlist: this.id});
					backlink = {name: 'watchlistDetail', params: {id: this.id}};
				} else if (this.entityId) {
					await this.$store.dispatch(FETCH_WATCHLIST, {entityId: this.entityId});
					backlink = {name: 'zohoWatchlist', params: {entityId: this.entityId}};
				} else if (this.hash) {
					await this.$store.dispatch(FETCH_WATCHLIST, {hash: this.hash});
					backlink = {name: 'sharedWatchlist', params: {hash: this.hash}};
				}
				if (this.displayedWatchlist && this.displayedWatchlist.dynamic) {
					this.$store.commit(SET_WATCHLIST_SEARCH_PARAMS, null);
					this.$store.commit(RESET_SEARCH_PARAMS, true);
					if (this.displayedWatchlist.searchParams) {
						await this.setSearchParams(this.displayedWatchlist);
						this.setSpeciesName();
					}
				}
				this.isLoading = false;
			} else {
				this.$store.commit(SET_WATCHLIST, this.propWatchlist);
			}

			if (this.displayedWatchlist && this.displayedWatchlist.dynamic && this.displayedWatchlist.searchParams) {
				this.$store.commit(RESET_SEARCH_RESULTS);
			}

			this.$store.commit(SET_DETAIL_VIEW_BACK_LINK, {name: this.$route.name, params: this.$route.params});
		},
		methods: {
			changeSortingDirection() {
				this.sortingDirection = (this.sortingDirection === 'DESC' ? 'ASC' : 'DESC');
			},
			setSorting(property) {
				if (property === this.sortingProperty) {
					this.sortingDirection = (this.sortingDirection === 'DESC' ? 'ASC' : 'DESC');
				} else {
					this.sortingDirection = 'DESC';
				}

				this.sortingProperty = property;
			},
			startSearchParamsEdit() {
				this.$store.commit(SET_EDIT_SEARCH_PARAMS, true);
				this.$store.commit(SET_DISPLAY_ADVANCED_SEARCH, true);
			},
			selectSearchParams() {
				this.startSearchParamsEdit();
				this.$store.commit(SET_WATCHLISTS, []);
				this.selectingParamsFromWatchlist = true;
				this.resetWatchlistsBeforeLeave = true;
			},
			async setSearchParams(watchlist) {
				this.$store.commit(RESET_SEARCH_PARAMS, true);
				if (watchlist.searchParams) {
					this.$store.commit(SET_DISPLAY_ADVANCED_SEARCH, true);
					const searchParams = JSON.parse(watchlist.searchParams);
          searchParams['defaultPreviewImageType'] = watchlist.showBundleImages ? imagetypes.SMALL_LOG : imagetypes.MANUAL_IMAGE_TEASER;

					this.$store.commit(SET_WATCHLIST_SEARCH_PARAMS, searchParams);
					this.$store.commit(SET_SEARCH_PARAMS, searchParams);
					if (this.user) {
						await this.$store.dispatch(GET_FILTER_DATA);
					}
				}
			},
			/**
			 * Recovers the actual species name from species list
			 */
			setSpeciesName() {
				if(this.speciesList.length) {
					const species = this.speciesList.find(element => element.uid === parseInt(this.watchlistSearchParams.species));
					this.$store.commit(SET_WATCHLIST_SEARCH_PARAMS, {species});
					this.$store.commit(SET_SEARCH_PARAMS, {species});
				}
			},
			newVirtualLog() {
				this.$store.commit(START_VIRTUAL_LOG_CREATION, this.user);
			}
		},
		beforeRouteLeave(to, from, next) {
			// reset watchlist list before leaving
			if(this.resetWatchlistsBeforeLeave) {
				this.$store.commit(SET_WATCHLISTS, []);
			}
			// reset search param edit state when leaving this route
			this.$store.commit(SET_EDIT_SEARCH_PARAMS, false);
			next();
		}
	}
</script>